import React, { useState } from "react";
import { useEffect } from "react";
import { supabase } from "../config/supabase";
import { User } from "@supabase/supabase-js";
import { fireDataRefresh, addDataRefreshListener } from "../utils/events";
import { ChildSelection } from "../types/child";
import ChildSelectionComponent from "./ChildSelection";
import { getGoalForDate, getDayOfWeek, getLocalDateString } from "../utils/goalUtils";

interface LogEntryProps {
  user: User | null;
  darkMode: boolean;
  onLogAdded?: () => void;
  treatmentType?: "self" | "child";
}

interface LogForm {
  date: string;
  hours: number;
  minutes: number;
  notes: string;
  selectedChildren: ChildSelection[];
}

interface DatePickerProps {
  value: string;
  onChange: (date: string) => void;
  darkMode: boolean;
}

// Custom Date Picker Component
const CustomDatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  darkMode,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(() => {
    if (value) {
      const [year, month, day] = value.split("-").map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date();
  });

  useEffect(() => {
    if (value) {
      // Parse date in local timezone to avoid timezone conversion issues
      const [year, month, day] = value.split("-").map(Number);
      const localDate = new Date(year, month - 1, day);
      setSelectedDate(localDate);
      setCurrentMonth(localDate);
    }
  }, [value]);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0,
  ).getDate();
  const firstDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1,
  ).getDay();

  const formatDateForDisplay = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateForInput = (date: Date) => {
    // Format in local timezone to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDateSelect = (day: number) => {
    const newDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );

    // Check if date is valid (not future and within 5 days)
    const today = new Date();
    const fiveDaysAgo = new Date(Date.now() - 5 * 24 * 60 * 60 * 1000);

    if (newDate <= today && newDate >= fiveDaysAgo) {
      setSelectedDate(newDate);
      onChange(formatDateForInput(newDate));
      setIsOpen(false);
    }
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + (direction === "next" ? 1 : -1));
      return newMonth;
    });
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === currentMonth.getMonth() &&
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  const isSelected = (day: number) => {
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === currentMonth.getMonth() &&
      selectedDate.getFullYear() === currentMonth.getFullYear()
    );
  };

  const isDisabled = (day: number) => {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );
    const today = new Date();
    const fiveDaysAgo = new Date(Date.now() - 5 * 24 * 60 * 60 * 1000);
    return date > today || date < fiveDaysAgo;
  };

  return (
    <div className="relative">
      {/* Date Input Display */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`group relative w-full px-4 py-4 border-2 rounded-xl transition-all duration-300 cursor-pointer hover:shadow-md ${
          darkMode
            ? "bg-gray-800 border-gray-600 hover:border-brand-cyan text-white"
            : "bg-white border-gray-300 hover:border-brand-dark-blue text-gray-900"
        } ${
          isOpen
            ? darkMode
              ? "border-brand-cyan"
              : "border-brand-dark-blue"
            : ""
        }`}
      >
        <div className="flex items-center justify-between">
          <span className="text-base font-medium">
            {formatDateForDisplay(selectedDate)}
          </span>
          <div
            className={`transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          >
            <svg
              className={`w-5 h-5 ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Calendar Dropdown */}
      {isOpen && (
        <div
          className={`absolute top-full mt-2 w-80 rounded-xl shadow-2xl border-2 z-50 ${
            darkMode
              ? "bg-gray-800 border-gray-600"
              : "bg-white border-gray-200"
          }`}
        >
          {/* Calendar Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-600">
            <button
              type="button"
              onClick={() => navigateMonth("prev")}
              className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                darkMode
                  ? "text-gray-300 hover:text-white"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <h3
              className={`text-lg font-semibold ${
                darkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>

            <button
              type="button"
              onClick={() => navigateMonth("next")}
              className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                darkMode
                  ? "text-gray-300 hover:text-white"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {/* Days of Week */}
          <div className="grid grid-cols-7 gap-1 p-2">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div
                key={day}
                className={`text-center text-sm font-medium py-2 ${
                  darkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1 p-2">
            {/* Empty cells for days before first day of month */}
            {Array.from({ length: firstDayOfMonth }, (_, i) => (
              <div key={`empty-${i}`} className="h-10"></div>
            ))}

            {/* Days of current month */}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const disabled = isDisabled(day);
              return (
                <button
                  key={day}
                  onClick={() => !disabled && handleDateSelect(day)}
                  disabled={disabled}
                  className={`h-10 w-10 rounded-lg text-sm font-medium transition-all duration-200 ${
                    disabled
                      ? "text-gray-400 cursor-not-allowed opacity-50"
                      : isSelected(day)
                        ? "bg-brand-dark-blue text-white shadow-lg"
                        : isToday(day)
                          ? darkMode
                            ? "bg-brand-cyan text-gray-900 font-bold"
                            : "bg-brand-cyan text-white font-bold"
                          : darkMode
                            ? "text-gray-300 hover:bg-gray-700 hover:text-white hover:scale-105"
                            : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:scale-105"
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Today Button */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-600">
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                setSelectedDate(today);
                setCurrentMonth(today);
                onChange(formatDateForInput(today));
                setIsOpen(false);
              }}
              className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                darkMode
                  ? "bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900"
              }`}
            >
              Select Today
            </button>
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
      )}
    </div>
  );
};

const LogEntry: React.FC<LogEntryProps> = ({
  user,
  darkMode,
  onLogAdded,
  treatmentType = "child",
}) => {
  const [logForm, setLogForm] = useState<LogForm>({
    date: getLocalDateString(),
    hours: 0,
    minutes: 0,
    notes: "",
    selectedChildren: [],
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  // Listen for child-related data refresh events
  useEffect(() => {
    const remove = addDataRefreshListener((event) => {
      const reason = event.detail?.reason;
      if (
        reason === "child_added" ||
        reason === "child_updated" ||
        reason === "child_deleted"
      ) {
        // Force re-render of ChildSelectionComponent
        setRefreshKey((prev) => prev + 1);
        setStatus(""); // Clear any existing status
      }
    });
    return remove;
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate time input
    if (totalMinutes === 0) {
      setStatus("Please enter the time spent on the activity.");
      return;
    }

    // Validate date
    const today = new Date();
    const todayStr = getLocalDateString(today);
    const fiveDaysAgo = new Date(today);
    fiveDaysAgo.setDate(today.getDate() - 5);
    const fiveDaysAgoStr = getLocalDateString(fiveDaysAgo);

    if (logForm.date < fiveDaysAgoStr) {
      setStatus("You can only log activities for the last 5 days.");
      return;
    }

    if (logForm.date > todayStr) {
      setStatus("You cannot log activities for future dates.");
      return;
    }

    // Check if children are selected (only for child treatment users)
    if (treatmentType === "child") {
      const selectedChildren = logForm.selectedChildren.filter(
        (child) => child.selected,
      );
      if (selectedChildren.length === 0) {
        setStatus("Please select at least one child for this activity.");
        return;
      }
    }

    // Check if notes are provided (now obligatory)
    if (!logForm.notes.trim()) {
      setStatus(
        "Session notes are required. Please add notes about your training session.",
      );
      return;
    }

    // Check if notes have minimum length of 10 characters
    if (logForm.notes.trim().length < 10) {
      setStatus(
        "Session notes must be at least 10 characters long. Please provide more detailed notes about your training session.",
      );
      return;
    }

    try {
      setLoading(true);
      setStatus("");

      if (treatmentType === "self") {
        // Fetch current user goals to save with log (including all daily goal columns)
        const { data: goalsData } = await supabase
          .from("user_goals")
          .select(
            "weekly_goal_minutes, monday_goal_minutes, tuesday_goal_minutes, wednesday_goal_minutes, thursday_goal_minutes, friday_goal_minutes, saturday_goal_minutes, sunday_goal_minutes",
          )
          .eq("user_id", user?.id)
          .single();

        // Get day-specific goal for the log date
        // null = rest day, number = goal in minutes
        const daySpecificGoal = goalsData
          ? getGoalForDate(goalsData, logForm.date) ?? 240
          : 240;
        const weeklyGoal = goalsData?.weekly_goal_minutes ?? 1680;
        const dayOfWeek = getDayOfWeek(logForm.date);

        // For self-treatment users, log directly to daily_logs
        const { error: insertError } = await supabase
          .from("daily_logs")
          .insert([
            {
              user_id: user?.id,
              date: logForm.date,
              time_spent_minutes: totalMinutes,
              notes: logForm.notes
                ? logForm.notes.trim().substring(0, 1000)
                : null,
              daily_goal_at_time: daySpecificGoal,
              weekly_goal_at_time: weeklyGoal,
              day_of_week_at_time: dayOfWeek,
            },
          ]);

        if (insertError) throw insertError;

        setStatus(
          `Added activity entry for yourself on ${
            logForm.date
          }. Duration: ${formatTime(totalMinutes)}`,
        );
      } else {
        // For child treatment users, log to child_logs
        const selectedChildren = logForm.selectedChildren.filter(
          (child) => child.selected,
        );
        const selectedChildIds = selectedChildren.map(
          (child) => child.child_id,
        );

        // Fetch goals for all selected children (including all daily goal columns)
        const { data: childGoalsData } = await supabase
          .from("child_goals")
          .select(
            "child_id, weekly_goal_minutes, monday_goal_minutes, tuesday_goal_minutes, wednesday_goal_minutes, thursday_goal_minutes, friday_goal_minutes, saturday_goal_minutes, sunday_goal_minutes",
          )
          .in("child_id", selectedChildIds);

        // Create a map of child_id to full goal objects
        const goalsMap = new Map(
          (childGoalsData || []).map((g) => [g.child_id, g]),
        );

        const dayOfWeek = getDayOfWeek(logForm.date);

        // Insert logs for each selected child with their day-specific goals
        const logsToInsert = selectedChildIds.map((childId) => {
          const childGoal = goalsMap.get(childId);
          // Get day-specific goal for this child and date
          // null = rest day, number = goal in minutes
          const daySpecificGoal: number | null = childGoal
            ? getGoalForDate(childGoal, logForm.date) ?? 240
            : 240;
          const weeklyGoal = childGoal?.weekly_goal_minutes ?? 1680;

          return {
            child_id: childId,
            date: logForm.date,
            time_spent_minutes: totalMinutes,
            notes: logForm.notes
              ? logForm.notes.trim().substring(0, 1000)
              : null,
            daily_goal_at_time: daySpecificGoal,
            weekly_goal_at_time: weeklyGoal,
            day_of_week_at_time: dayOfWeek,
          };
        });

        const { error: insertError } = await supabase
          .from("child_logs")
          .insert(logsToInsert);

        if (insertError) throw insertError;

        const childNames = selectedChildren
          .map((child) => child.name)
          .join(", ");

        setStatus(
          `Added activity entry for ${childNames} on ${
            logForm.date
          }. Duration: ${formatTime(totalMinutes)}`,
        );
      }

      // Reset form
      setLogForm({
        date: getLocalDateString(),
        hours: 0,
        minutes: 0,
        notes: "",
        selectedChildren: [],
      });

      // Call callback if provided
      if (onLogAdded) {
        onLogAdded();
      }

      // Broadcast a refresh event so other tabs (dashboard, calendar, statistics) update
      fireDataRefresh({ reason: "log_added" });
    } catch (error: any) {
      // Only log detailed errors in development
      if (process.env.NODE_ENV === "development") {
        console.error("Error saving log:", error);
        console.error("Error details:", {
          message: error?.message,
          details: error?.details,
          hint: error?.hint,
          code: error?.code,
          statusCode: error?.status,
          fullError: error,
        });
      }

      // Check for specific error messages from database triggers/constraints
      // Supabase errors can be in different formats
      const errorMessage = (
        error?.message ||
        error?.details ||
        error?.hint ||
        error?.toString() ||
        ""
      ).toLowerCase();

      if (
        errorMessage.includes("duplicate") ||
        errorMessage.includes("unique")
      ) {
        setStatus(
          "An activity entry already exists for this date. Please update the existing entry instead.",
        );
      } else if (
        errorMessage.includes("foreign key") ||
        errorMessage.includes("constraint")
      ) {
        setStatus(
          "Invalid child selection. Please refresh the page and try again.",
        );
      } else if (errorMessage.includes("time_spent_minutes")) {
        setStatus(
          "Invalid time duration. Please enter a valid time between 1 and 1440 minutes.",
        );
      } else {
        setStatus(
          `Failed to save activity: ${error?.message || "Unknown error"}`,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round((minutes % 60) * 10) / 10; // Round to 1 decimal
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const handleQuickTime = (hours: number, minutes: number = 0) => {
    setLogForm({
      ...logForm,
      hours,
      minutes,
    });
  };

  const totalMinutes = logForm.hours * 60 + logForm.minutes;

  return (
    <div
      className={`rounded-lg p-6 ${
        darkMode ? "bg-gray-800" : "bg-white"
      } shadow-lg`}
    >
      <h3
        className={`text-xl font-semibold mb-4 ${
          darkMode ? "text-white" : "text-gray-900"
        }`}
      >
        Log Your Activity
      </h3>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Layout: Two columns for child, single column for self */}
        {treatmentType === "child" ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column: Child Selection */}
            <div>
              <ChildSelectionComponent
                key={refreshKey}
                user={user}
                darkMode={darkMode}
                onSelectionChange={(selectedChildren) =>
                  setLogForm({ ...logForm, selectedChildren })
                }
              />
            </div>
            {/* Right Column: Date and Time */}
            <div className="space-y-6">
              {/* Modern Date Picker */}
              <div>
                <label
                  className={`block text-sm font-semibold mb-3 ${
                    darkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  📅 Activity Date
                </label>
                <CustomDatePicker
                  value={logForm.date}
                  onChange={(date) => setLogForm({ ...logForm, date })}
                  darkMode={darkMode}
                />
              </div>
              {/* Modern Time Dropdowns */}
              <div className="grid grid-cols-2 gap-4">
                {/* Hours Dropdown */}
                <div>
                  <label
                    className={`block text-sm font-semibold mb-3 ${
                      darkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    ⏰ Hours
                  </label>
                  <div className="relative">
                    <select
                      value={logForm.hours}
                      onChange={(e) =>
                        setLogForm({
                          ...logForm,
                          hours: parseInt(e.target.value),
                        })
                      }
                      className={`w-full px-4 py-4 border-2 rounded-xl text-lg font-medium appearance-none cursor-pointer transition-all duration-300 hover:shadow-md focus:ring-4 focus:ring-brand-cyan/20 focus:border-brand-cyan ${
                        darkMode
                          ? "bg-gray-800 border-gray-600 text-white hover:border-brand-cyan"
                          : "bg-white border-gray-300 text-gray-900 hover:border-brand-cyan hover:shadow-brand-cyan/10"
                      }`}
                    >
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((hour) => (
                        <option key={hour} value={hour}>
                          {hour} {hour === 1 ? "hour" : "hours"}
                        </option>
                      ))}
                    </select>
                    {/* Custom Dropdown Arrow */}
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <div
                        className={`p-2 rounded-lg ${
                          darkMode
                            ? "bg-gray-700 text-gray-300"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Minutes Dropdown */}
                <div>
                  <label
                    className={`block text-sm font-semibold mb-3 ${
                      darkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    ⏱️ Minutes
                  </label>
                  <div className="relative">
                    <select
                      value={logForm.minutes}
                      onChange={(e) =>
                        setLogForm({
                          ...logForm,
                          minutes: parseInt(e.target.value),
                        })
                      }
                      className={`w-full px-4 py-4 border-2 rounded-xl text-lg font-medium appearance-none cursor-pointer transition-all duration-300 hover:shadow-md focus:ring-4 focus:ring-brand-cyan/20 focus:border-brand-cyan ${
                        darkMode
                          ? "bg-gray-800 border-gray-600 text-white hover:border-brand-cyan"
                          : "bg-white border-gray-300 text-gray-900 hover:border-brand-cyan hover:shadow-brand-cyan/10"
                      }`}
                    >
                      {[0, 10, 20, 30, 40, 50].map((minute) => (
                        <option key={minute} value={minute}>
                          {minute} minutes
                        </option>
                      ))}
                    </select>
                    {/* Custom Dropdown Arrow */}
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <div
                        className={`p-2 rounded-lg ${
                          darkMode
                            ? "bg-gray-700 text-gray-300"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Modern Date Picker */}
            <div>
              <label
                className={`block text-sm font-semibold mb-3 ${
                  darkMode ? "text-gray-200" : "text-gray-800"
                }`}
              >
                📅 Activity Date
              </label>
              <CustomDatePicker
                value={logForm.date}
                onChange={(date) => setLogForm({ ...logForm, date })}
                darkMode={darkMode}
              />
            </div>
            {/* Modern Time Dropdowns */}
            <div className="grid grid-cols-2 gap-4">
              {/* Hours Dropdown */}
              <div>
                <label
                  className={`block text-sm font-semibold mb-3 ${
                    darkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  ⏰ Hours
                </label>
                <div className="relative">
                  <select
                    value={logForm.hours}
                    onChange={(e) =>
                      setLogForm({
                        ...logForm,
                        hours: parseInt(e.target.value),
                      })
                    }
                    className={`w-full px-4 py-4 border-2 rounded-xl text-lg font-medium appearance-none cursor-pointer transition-all duration-300 hover:shadow-md focus:ring-4 focus:ring-brand-cyan/20 focus:border-brand-cyan ${
                      darkMode
                        ? "bg-gray-800 border-gray-600 text-white hover:border-brand-cyan"
                        : "bg-white border-gray-300 text-gray-900 hover:border-brand-cyan hover:shadow-brand-cyan/10"
                    }`}
                  >
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((hour) => (
                      <option key={hour} value={hour}>
                        {hour} {hour === 1 ? "hour" : "hours"}
                      </option>
                    ))}
                  </select>
                  {/* Custom Dropdown Arrow */}
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <div
                      className={`p-2 rounded-lg ${
                        darkMode
                          ? "bg-gray-700 text-gray-300"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              {/* Minutes Dropdown */}
              <div>
                <label
                  className={`block text-sm font-semibold mb-3 ${
                    darkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  ⏱️ Minutes
                </label>
                <div className="relative">
                  <select
                    value={logForm.minutes}
                    onChange={(e) =>
                      setLogForm({
                        ...logForm,
                        minutes: parseInt(e.target.value),
                      })
                    }
                    className={`w-full px-4 py-4 border-2 rounded-xl text-lg font-medium appearance-none cursor-pointer transition-all duration-300 hover:shadow-md focus:ring-4 focus:ring-brand-cyan/20 focus:border-brand-cyan ${
                      darkMode
                        ? "bg-gray-800 border-gray-600 text-white hover:border-brand-cyan"
                        : "bg-white border-gray-300 text-gray-900 hover:border-brand-cyan hover:shadow-brand-cyan/10"
                    }`}
                  >
                    {[0, 10, 20, 30, 40, 50].map((minute) => (
                      <option key={minute} value={minute}>
                        {minute} minutes
                      </option>
                    ))}
                  </select>
                  {/* Custom Dropdown Arrow */}
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <div
                      className={`p-2 rounded-lg ${
                        darkMode
                          ? "bg-gray-700 text-gray-300"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notes Field (Now Obligatory) */}
        <div>
          <label
            className={`block text-sm font-semibold mb-3 ${
              darkMode ? "text-gray-200" : "text-gray-800"
            }`}
          >
            📝 Session Notes (Required)
          </label>
          <div className="relative">
            <textarea
              value={logForm.notes}
              onChange={(e) =>
                setLogForm({ ...logForm, notes: e.target.value })
              }
              placeholder="Add notes about your training session (required)..."
              rows={4}
              maxLength={1000}
              required
              className={`w-full px-4 py-4 border-2 rounded-xl resize-none transition-all duration-300 hover:shadow-md focus:ring-4 focus:ring-brand-cyan/20 focus:border-brand-cyan ${
                darkMode
                  ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400 hover:border-brand-cyan"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 hover:border-brand-cyan hover:shadow-brand-cyan/10"
              }`}
            />
            <div
              className={`absolute bottom-2 right-2 text-xs ${
                darkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {logForm.notes.length}/1000
            </div>
          </div>
        </div>

        {/* Quick Time Buttons */}
        <div>
          <label
            className={`block text-sm font-semibold mb-3 ${
              darkMode ? "text-gray-200" : "text-gray-800"
            }`}
          >
            ⚡ Quick Select
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button
              type="button"
              onClick={() => handleQuickTime(1, 30)}
              className={`px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 hover:shadow-md transform hover:scale-105 ${
                darkMode
                  ? "bg-gray-700 text-white hover:bg-gray-600 border border-gray-600"
                  : "bg-gray-50 text-gray-900 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              <div className="flex items-center justify-center space-x-1">
                <span>⏰</span>
                <span>1.5h</span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleQuickTime(2, 0)}
              className={`px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 hover:shadow-md transform hover:scale-105 ${
                darkMode
                  ? "bg-gray-700 text-white hover:bg-gray-600 border border-gray-600"
                  : "bg-gray-50 text-gray-900 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              <div className="flex items-center justify-center space-x-1">
                <span>🕐</span>
                <span>2h</span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleQuickTime(3, 0)}
              className={`px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 hover:shadow-md transform hover:scale-105 ${
                darkMode
                  ? "bg-gray-700 text-white hover:bg-gray-600 border border-gray-600"
                  : "bg-gray-50 text-gray-900 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              <div className="flex items-center justify-center space-x-1">
                <span>🕒</span>
                <span>3h</span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleQuickTime(4, 0)}
              className={`px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 hover:shadow-md transform hover:scale-105 ${
                darkMode
                  ? "bg-gray-700 text-white hover:bg-gray-600 border border-gray-600"
                  : "bg-gray-50 text-gray-900 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              <div className="flex items-center justify-center space-x-1">
                <span>🕓</span>
                <span>4h</span>
              </div>
            </button>
          </div>
        </div>

        {/* Modern Submit Button */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={loading || totalMinutes <= 0}
            className={`relative w-full text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${
              darkMode
                ? "bg-brand-cyan hover:bg-brand-dark-blue"
                : "bg-brand-dark-blue hover:bg-brand-cyan"
            }`}
          >
            {/* Button content */}
            <div className="flex items-center justify-center space-x-3">
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  <span className="text-lg">Saving...</span>
                </>
              ) : (
                <>
                  <span className="text-2xl">💾</span>
                  <span className="text-lg">Save Activity Log</span>
                  {totalMinutes > 0 && (
                    <span className="text-sm opacity-90">
                      ({formatTime(totalMinutes)})
                    </span>
                  )}
                </>
              )}
            </div>
          </button>
        </div>

        {status && (
          <div
            className={`p-3 rounded-lg text-sm font-medium ${
              status.includes("Added") || status.includes("Updated")
                ? darkMode
                  ? "bg-green-900 text-green-300 border border-green-700"
                  : "bg-green-50 text-green-800 border border-green-200"
                : darkMode
                  ? "bg-red-900 text-red-300 border border-red-700"
                  : "bg-red-50 text-red-800 border border-red-200"
            }`}
          >
            {status}
          </div>
        )}
      </form>
    </div>
  );
};

export default LogEntry;
